import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import LanguageToggle from '../components/LanguageToggle';
import { ArrowRight, Play, Shield, BookOpen } from 'lucide-react';

const Landing = () => {
  const { t, language } = useLanguage();

  const categories = [
    { icon: Shield, name_en: 'PPE Safety', name_ar: 'سلامة معدات الحماية' },
    { icon: Shield, name_en: 'Fire Safety', name_ar: 'سلامة مكافحة الحرائق' },
    { icon: Shield, name_en: 'First Aid', name_ar: 'الإسعافات الأولية' },
    { icon: Shield, name_en: 'Electrical Safety', name_ar: 'السلامة الكهربائية' },
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-heading text-2xl font-black tracking-tight text-safety-amber">
            {t('Anime Safety Guide', 'دليل السلامة الأنيمي')}
          </h1>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login">
              <Button data-testid="admin-login-link" variant="ghost" className="text-[#A1A1AA] hover:text-safety-amber">
                {t('Admin', 'مسؤول')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-safety-amber/5 to-transparent" />
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-[#EDEDED] leading-none mb-6">
            {t(
              'Learn Safety Through Anime',
              'تعلم السلامة من خلال الأنيمي'
            )}
          </h2>
          <p className="text-xl text-[#A1A1AA] mb-8 leading-relaxed">
            {t(
              'Daily safety tips and guidelines presented with engaging anime-style visuals',
              'نصائح وإرشادات السلامة اليومية مقدمة مع مرئيات جذابة بنمط الأنيمي'
            )}
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              data-testid="watch-videos-button"
              className="bg-safety-amber hover:bg-safety-amber/90 text-obsidian font-bold text-lg px-8 py-6 rounded-lg transition-transform hover:scale-105"
            >
              <Play className="w-5 h-5 me-2" />
              {t('Watch Videos', 'شاهد الفيديوهات')}
            </Button>
            <Button
              data-testid="explore-topics-button"
              variant="outline"
              className="border-mecha-blue text-mecha-blue hover:bg-mecha-blue/10 font-bold text-lg px-8 py-6 rounded-lg"
            >
              {t('Explore Topics', 'استكشف المواضيع')}
              <ArrowRight className="w-5 h-5 ms-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 bg-carbon/30">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="font-heading text-3xl font-black text-safety-amber mb-6">
            {t('Our Mission', 'مهمتنا')}
          </h3>
          <p className="text-lg text-[#EDEDED] leading-relaxed">
            {t(
              'Making workplace safety education accessible and engaging for everyone through creative anime-style content. Our daily videos cover essential safety topics in both English and Arabic.',
              'جعل تعليم السلامة في مكان العمل متاحًا وجذابًا للجميع من خلال محتوى إبداعي بنمط الأنيمي. تغطي مقاطع الفيديو اليومية الخاصة بنا مواضيع السلامة الأساسية باللغتين الإنجليزية والعربية.'
            )}
          </p>
        </div>
      </section>

      {/* Safety Categories */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <h3 className="font-heading text-3xl font-black text-[#EDEDED] text-center mb-12">
            {t('Safety Topics We Cover', 'مواضيع السلامة التي نغطيها')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categories.map((category, idx) => {
              const Icon = category.icon;
              return (
                <div
                  key={idx}
                  data-testid={`category-${idx}`}
                  className="bg-carbon border border-[#27272A] rounded-lg p-6 text-center hover:border-mecha-blue/30 transition-all hover:scale-105 cursor-pointer"
                >
                  <Icon className="w-12 h-12 text-safety-amber mx-auto mb-4" />
                  <h4 className="font-semibold text-[#EDEDED]">
                    {language === 'ar' ? category.name_ar : category.name_en}
                  </h4>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-8 px-6 text-center text-[#A1A1AA]">
        <p>© 2026 Anime Safety Guide. {t('All rights reserved', 'جميع الحقوق محفوظة')}.</p>
      </footer>
    </div>
  );
};

export default Landing;
